/**
 * Authentication Redux Slice
 * 
 * Manages authentication state using Redux Toolkit.
 * Handles user data, loading states, and errors.
 */

import { createSlice } from "@reduxjs/toolkit";
import {
  loginThunk,
  registerThunk,
  getCurrentUserThunk,
  updateProfileThunk,
  logoutThunk,
  changePasswordThunk,
  becomeWriterThunk,
} from "./authThunks";

const initialState = {
  user: null,
  loading: false,
  error: null,
  isAuthenticated: false,
  isInitializing: true, // Track if auth state is being initialized
  lastAction: null, // Track last action for better debugging
  lastActionTime: null, // Track when last action occurred
};

const PRIVILEGED_USER_FIELDS = new Set([
  "role",
  "roles",
  "subscriptions",
]);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    // Clear error message
    clearError: (state) => {
      state.error = null;
      state.lastAction = "clearError";
      state.lastActionTime = Date.now();
    },
    
    // Clear loading state
    clearLoading: (state) => {
      state.loading = false;
    },
    
    // Set user (for auth state changes)
    setUser: (state, action) => {
      state.user = action.payload;
      state.isAuthenticated = !!action.payload;
      state.error = null;
      state.isInitializing = false; // Auth state has been initialized
      state.lastAction = "setUser";
      state.lastActionTime = Date.now();
    },
    
    // Clear user (for logout)
    clearUser: (state) => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.loading = false;
      state.isInitializing = false; // Auth state has been initialized
      state.lastAction = "clearUser";
      state.lastActionTime = Date.now();
    },
    
    // Set initialization state
    setInitializing: (state, action) => {
      state.isInitializing = action.payload;
    },
    
    // Update user profile (for profile updates)
    updateUserProfile: (state, action) => {
      if (state.user) {
        // Guard against accidental client-side privilege escalation via local state.
        const safePayload = Object.fromEntries(
          Object.entries(action.payload || {}).filter(([key]) => !PRIVILEGED_USER_FIELDS.has(key))
        );
        state.user = { ...state.user, ...safePayload };
        state.lastAction = "updateUserProfile";
        state.lastActionTime = Date.now();
      }
    }
  },
  extraReducers: (builder) => {
    builder
      // ✅ LOGIN THUNK
      .addCase(loginThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.lastAction = "login.pending";
        state.lastActionTime = Date.now();
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
        state.lastAction = "login.fulfilled";
        state.lastActionTime = Date.now();
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Login failed";
        state.user = null;
        state.isAuthenticated = false;
        state.lastAction = "login.rejected";
        state.lastActionTime = Date.now();
      })
      
      // ✅ REGISTER THUNK
      .addCase(registerThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(registerThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = true;
        state.error = null;
      })
      .addCase(registerThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Registration failed";
        state.user = null;
        state.isAuthenticated = false;
      })
      
      // ✅ GET CURRENT USER THUNK
      .addCase(getCurrentUserThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCurrentUserThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload;
        state.isAuthenticated = !!action.payload;
        state.error = null;
      })
      .addCase(getCurrentUserThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load user";
        state.user = null;
        state.isAuthenticated = false;
      })
      
      // ✅ UPDATE PROFILE THUNK
      .addCase(updateProfileThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfileThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Merge updated profile data with existing user data
        state.user = { 
          ...state.user, 
          ...action.payload,
          // Ensure uid is preserved
          uid: state.user?.uid || action.payload?.uid
        };
        state.error = null;
      })
      .addCase(updateProfileThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to update profile";
      })
      
      // ✅ LOGOUT THUNK
      .addCase(logoutThunk.pending, (state) => {
        state.loading = true;
      })
      .addCase(logoutThunk.fulfilled, (state) => {
        state.loading = false;
        state.user = null;
        state.isAuthenticated = false;
        state.error = null;
      })
      .addCase(logoutThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Logout failed";
      })
      
      // ✅ CHANGE PASSWORD THUNK
      .addCase(changePasswordThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(changePasswordThunk.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(changePasswordThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to change password";
      })

      // ✅ BECOME WRITER THUNK
      .addCase(becomeWriterThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(becomeWriterThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.user = { ...state.user, ...action.payload };
        state.error = null;
      })
      .addCase(becomeWriterThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to become a writer";
      });
  },
});

export const {
  clearError,
  clearLoading,
  setUser,
  clearUser,
  updateUserProfile,
  setInitializing,
} = authSlice.actions;

export default authSlice.reducer;

// ✅ Selectors for easy access to auth state
export const selectAuth = (state) => state.auth;
export const selectUser = (state) => state.auth.user;
export const selectAuthLoading = (state) => state.auth.loading;
export const selectAuthError = (state) => state.auth.error;
export const selectIsAuthenticated = (state) => state.auth.isAuthenticated;
export const selectIsInitializing = (state) => state.auth.isInitializing;

